import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  Logging,
  Notification,
  API,
  ResponseError,
  NotificationData,
  Certificate,
  ControlState,
  renderCheckbox,
  renderTextArea,
  settingRejectionReason,
  SettingsContextObject,
  CertificateRejectionDetail,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon, VtxModal } from '@vertexinc/vtx-ui-react-component-library';
import CertificateRejectionViewerProps from './CertificateRejectionViewerProps.interface';
import '../sidebyside-viewer.scss';
import '../certificate-rejection-viewer.scss';
import initializeArray from '../../helpers/CertificateRejectionViewer';
import useCertificate from '../SideBySideViewer/useCertificate';

const bodyStyle = {
  backgroundColor: 'white',
};

const CertificateRejectionViewer = ({
  isOpen,
  certificateUuid,
  certificateId,
  certificate,
  gatewayEndPoint,
  onRejected,
  onCancel,
}: CertificateRejectionViewerProps): JSX.Element => {
  const { settingsContext, setSettingsContext } = React.useContext(SettingsContextObject);
  const [rejectReasons, setRejectReasons] = React.useState(null);
  const [rejectReasonsValues, setRejectReasonsValues] = React.useState(null);
  const [sendEmailToCustomer, setSendEmailToCustomer] = React.useState(true);
  const [additionalInformation, setAdditionalInformation] = React.useState(null);
  const [notification, setNotification] = React.useState(null);
  const [certificateInternal, setCertificateInternal] = React.useState(certificate);

  const dispatch = useDispatch();

  const controlState: ControlState = {
    editable: true,
    disabled: false,
    readOnly: false,
  };

  const getCertificate = () => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      const certificateApi: Certificate = await api.certificate.getCertificateV41(certificateUuid);

      Logging.Info('Reject Certificate:', certificateApi);
      setCertificateInternal(certificateApi);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Certificate Read Error',
        description: `Unable to read Certificate ${certificateId}.`,
        error: error as ResponseError,
      };
      setNotification(notificationData);
    }
  };

  // Make the API Call to Reject the Certificate
  const rejectCertificate = () => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);
      const reasons: string[] = [];
      let index = 0;

      // Loop through the Reasons
      rejectReasons.forEach((rejectReason: string) => {
        // If selected, add the Reason Id to the List
        if (rejectReasonsValues[index] === true) {
          reasons.push(rejectReason);
        }

        index += 1;
      });

      const rejectionDetail: CertificateRejectionDetail = {
        reasons,
        additionalInfo: additionalInformation,
        sendEmail: sendEmailToCustomer,
      };

      Logging.Info('Reject Certificate', certificateUuid, rejectionDetail, rejectReasonsValues);

      await api.certificate.rejectUserCertificateV41(certificateUuid, rejectionDetail);

      // Inform our Parent that Certificate was rejected
      onRejected(certificateInternal);
    } catch (error) {
      const responseError: ResponseError = error as ResponseError;

      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Certificate Approval Error',
        description: `Unable to reject Certificate '${certificateId}'.`,
        error: error as ResponseError,
        appendMessagesForStatusCodes: [409],
        hideStatusCodes: [409],
      };
      setNotification(notificationData);
    }
  };

  const onReject = (): void => {
    Logging.Info('Certificate Rejection Viewer: Reject');

    // Dispatch the Asynchronous Event
    dispatch(rejectCertificate());
  };

  const onCancelRejectionViewer = (): void => {
    Logging.Info('Certificate Rejection Viewer: Close');

    // Inform our Parent that Rejection Viewer was cancelled
    onCancel();
  };

  const renderReasons = (): JSX.Element | JSX.Element[] => {
    if (!rejectReasons || !rejectReasonsValues) {
      return (
        <span>Error: Unable to Load the Rejection Reasons</span>
      );
    }

    let index = -1;

    return rejectReasons.map((reason: string) => {
      const id: string = reason;
      index += 1;

      return (renderCheckbox(id,
        reason,
        rejectReasonsValues[index],
        index,
        setRejectReasonsValues,
        controlState,
        'crViewerRejectionReason'));
    });
  };

  // Get the Certificate Reject Reasons from the Certificate Settings
  const getRejectionReasons = (): string[] => {
    const reasons: string[] = [];

    if (settingsContext && settingsContext.certificates) {
      Object.keys(settingsContext.certificates).forEach((key: string) => {
        if (settingsContext.certificates[key] && key.startsWith(settingRejectionReason)) {
          reasons.push(settingsContext.certificates[key]);
        }
      });
    }

    Logging.Info('Rejection Reasons:', reasons);

    return reasons;
  };

  React.useEffect(() => {
    // Get the Rejection Reasons
    const reasons: string[] = getRejectionReasons();
    setRejectReasons(reasons);

    // Initialize the Value Array for the Checkboxes
    const values: boolean[] = initializeArray(reasons ? reasons.length : 0);
    setRejectReasonsValues(values);

    // Load the Certificate if it is not passed
    if (!certificateInternal) {
      dispatch(getCertificate());
    }

    return (): void => {
      // This Code will run once the component unmounts.
      setNotification(null);
    };
  }, []);

  // Not Open
  if (!isOpen) {
    return (<></>);
  }

  // Error has occured, show it
  if (notification) {
    return (
      <Notification
        data={notification}
        onNotificationClose={onCancelRejectionViewer}
        autoClose
      />
    );
  }

  const Spacer = () => <span style={{ marginRight: '10px' }} />;

  Logging.Debug('Certificate Rejection Viewer', certificateUuid, certificateId);

  // Check if at least one Rejection Reason is selected
  const canReject: boolean = rejectReasonsValues ? rejectReasonsValues.filter((value: boolean) => (value === true)).length > 0 : false;

  return (
    <div>
      <VtxModal
        visible={isOpen}
        onCancel={onCancelRejectionViewer}
        bodyStyle={bodyStyle}
        width="400px"
        footer={null}
        closable={false}
      >
        <div className="crViewerContainer">
          <div className="crViewerHeader">
            Rejection Reason
          </div>
          <div className="crViewerRejectionQuestion">
            Why are you rejecting this certificate?
          </div>
          <div className="crViewerRejectionReasons">
            {renderReasons()}
          </div>
          <div className="crViewerAdditionalInformationContainer">
            <div className="crViewerAdditionalInformation">
              Additional Information
            </div>
            <div className="crViewerAdditionalInformationOptional">
              Optional
            </div>
          </div>
          {renderTextArea(
            'additionalInfo',
            null,
            additionalInformation,
            'Provide more context to explain the rejection...',
            4,
            setAdditionalInformation,
            controlState,
            null,
          )}
          <div className="crViewerNotifyCustomerQuestion">
            Notify Customer?
          </div>
          {renderCheckbox(
            'sendEmail',
            'Send email correspondence to customer',
            sendEmailToCustomer,
            null,
            setSendEmailToCustomer,
            controlState,
            'crViewerRejectionReason',
          )}
          <div className="crViewerGrayLine" />
          <div className="crViewerButtons">
            <VtxButton
              id="rejectButton"
              icon={<VtxIcon type="stop" />}
              type="primary"
              className="redButton"
              disabled={!canReject}
              onClick={onReject}
            >
              Reject
            </VtxButton>
            <Spacer />
            <VtxButton
              id="cancelButton"
              type="default"
              onClick={onCancelRejectionViewer}
            >
              Cancel
            </VtxButton>
          </div>
        </div>
      </VtxModal>
    </div>
  );
};

export default CertificateRejectionViewer;
